import { Loader2, Unplug } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { otworzPulpit, stanPulpitu } from '@/lib/machines-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PULPIT MASZYNY NA ŻYWO — sam obraz, bez ani jednego paska.
 *
 *  ⚠️⚠️ SERWER RFB JEST CZĘŚCIĄ AGENTA (rozstrzygnięte 15.09.2026: „ma po prostu być częścią
 *  agenta, a nie doinstalowanie TightVNC"), a po tej stronie stoi gotowy klient — noVNC. Obraz
 *  idzie tunelem: agent otwiera gniazdo DO instancji, bo automaty stoją za naszym LTE, bez adresu
 *  publicznego.
 *
 *  ⚠️⚠️ HASŁO PODAJEMY ZA CZŁOWIEKA. Jest jednorazowe, ważne tylko dla tej sesji i przychodzi
 *  w odpowiedzi na otwarcie — pytanie o nie operatora byłoby przepisywaniem ośmiu znaków z ekranu
 *  na ekran, bez ani jednego grama bezpieczeństwa w zamian.
 *
 *  ⚠️⚠️ ŻADNEGO PASKA NAD ANI POD OBRAZEM — zgłoszone 15.09.2026 dwa razy pod rząd: „ten dolny
 *  biały pasek też wywal, szkoda miejsca w pionie, a nic nie wnosi" i „górny biały pasek też
 *  wywal". Ekrany automatów bywają STOJĄCE (PESA-3 melduje 1080×1920), więc każdy pasek zabiera
 *  wysokość tam, gdzie jej najbardziej brakuje. Komunikaty leżą NA obrazie, a nie obok niego.
 *
 *  ⚠️ MONTUJE SIĘ DOPIERO, GDY KTOŚ GO OTWORZY: samo wejście URUCHAMIA serwer na maszynie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/** Co okno może zrobić z ŻYWĄ sesją — dostaje to dopiero, gdy obraz naprawdę przyszedł. */
export type SterowanieSesja = {
  /** Numer sesji — potrzebny, bo sterowanie idzie trasą tej konkretnej sesji, a nie maszyny. */
  sesja: string;
  wyslijSchowek: (tekst: string) => void;
  /**
   * ⚠️ SAMO USTAWIENIE SCHOWKA NICZEGO NIE WKLEJA. Program na maszynie czeka na Ctrl+V, a my go
   * przechwyciliśmy, żeby zdążyć wysłać treść — więc musimy go teraz wysłać sami.
   */
  wklej: () => void;
};

export function PulpitMaszyny({
  machineId,
  groupId,
  onMaszyna,
  onRozmiar,
  onSterowanie,
  onSchowekZMaszyny,
}: {
  machineId: string;
  groupId: string;
  /** Nazwa automatu prosto z odpowiedzi portalu — okno robi z niej swój tytuł. */
  onMaszyna?: (nazwa: string) => void;
  /** Rozdzielczość ekranu maszyny, gdy obraz już przyszedł — okno dopasowuje do niej swój rozmiar. */
  onRozmiar?: (r: { szerokosc: number; wysokosc: number }) => void;
  /** Sterowanie sesją dla paska skrótów; `null` = sesji nie ma i przyciski mają być martwe. */
  onSterowanie?: (s: SterowanieSesja | null) => void;
  /** Tekst skopiowany NA MASZYNIE — okno pokaże go, gdy przeglądarka nie wpuści go do schowka. */
  onSchowekZMaszyny?: (tekst: string) => void;
}) {
  const { t } = useI18n();
  const ekran = useRef<HTMLDivElement>(null);
  const rfb = useRef<{ disconnect: () => void } | null>(null);
  const [stan, setStan] = useState<'OTWIERAM' | 'CZEKAM' | 'OBRAZ' | 'KONIEC'>('OTWIERAM');
  const [komunikat, setKomunikat] = useState<string | null>(null);
  const [wezel, setWezel] = useState<string | null>(null);
  /**
   * ⚠️⚠️ CO MÓWI PORTAL I CO MÓWI AGENT — dołożone po pierwszej próbie na testówce (15.09.2026).
   * Na trzech maszynach okno pokazywało „Połączenie zerwane" i nic więcej, a portal cały czas
   * znał powód: sesja bez tunelu, błąd agenta przy stawianiu serwera albo limit czasu. Teraz
   * okno o to pyta — dopóki nie ma obrazu i jeszcze chwilę po rozłączeniu.
   */
  const [odPortalu, setOdPortalu] = useState<{ powod: string | null; zadanie: string | null }>({
    powod: null,
    zadanie: null,
  });
  const idSesji = useRef<string | null>(null);

  useEffect(() => {
    let zywe = true;

    void (async () => {
      let sesja: Awaited<ReturnType<typeof otworzPulpit>>;
      try {
        sesja = await otworzPulpit(groupId, machineId);
      } catch (e) {
        if (!zywe) return;
        setStan('KONIEC');
        setKomunikat(e instanceof ApiError ? e.message : t('pulpit.nieUdaloSie'));
        return;
      }
      if (!zywe) return;
      idSesji.current = sesja.sesja;
      setWezel(sesja.nodeId);
      onMaszyna?.(sesja.maszyna);
      setStan('CZEKAM');

      /**
       * ⚠️⚠️ `import()` W ŚRODKU, NIE NA GÓRZE PLIKU. noVNC to kilkaset kilobajtów kodu, którego
       * nie potrzebuje nikt, kto nie otwiera pulpitu — a ekran automatów otwiera się non stop.
       * Przy imporcie na górze ta paczka wchodziłaby do głównego bundla portalu.
       */
      const { default: RFB } = await import('@novnc/novnc');
      if (!zywe) return;

      const adres = new URL(`/api/pulpit/${sesja.sesja}`, window.location.origin);
      adres.protocol = adres.protocol === 'https:' ? 'wss:' : 'ws:';

      const polaczenie = new RFB(ekran.current!, adres.toString(), { credentials: { password: sesja.haslo } });
      polaczenie.scaleViewport = true;
      polaczenie.clipViewport = true;
      /** ⚠️ Sterowanie jest sensem tego okna — to nasze maszyny, a nie podgląd cudzego biurka. */
      polaczenie.viewOnly = false;

      /**
       * ══════════════════════════════════════════════════════════════════════════════════════════
       *  SCHOWEK Z MASZYNY DO PRZEGLĄDARKI — automatycznie, bez klikania.
       *
       *  ⚠️⚠️ W DRUGĄ STRONĘ AUTOMATU BYĆ NIE MOŻE i to nie jest niedbalstwo. noVNC przechwytuje
       *  klawiaturę i woła `preventDefault()` na każdym wciśnięciu, więc przeglądarka NIGDY nie
       *  zrobi z Ctrl+V zdarzenia wklejenia. A odczyt schowka z kodu (`navigator.clipboard.readText`)
       *  jest w Firefoksie zamknięty dla zwykłych stron. Dlatego wysyłka schowka ma swój przycisk,
       *  a nie udaje, że działa skrótem klawiszowym.
       *
       *  ⚠️ `writeText` BYWA ODMÓWIONY (brak gestu, brak uprawnienia) — wtedy pokazujemy tekst
       *  w oknie zamiast twierdzić, że wylądował w schowku.
       * ══════════════════════════════════════════════════════════════════════════════════════════
       */
      polaczenie.addEventListener('clipboard', (e: CustomEvent<{ text: string }>) => {
        if (!zywe || typeof e.detail.text !== 'string' || e.detail.text === '') return;
        void navigator.clipboard.writeText(e.detail.text).catch(() => onSchowekZMaszyny?.(e.detail.text));
      });

      polaczenie.addEventListener('connect', () => {
        if (!zywe) return;
        setStan('OBRAZ');
        /** ⚠️ Sterowanie oddajemy DOPIERO po połączeniu — wcześniej te wywołania nie mają dokąd iść. */
        onSterowanie?.({
          sesja: sesja.sesja,
          wyslijSchowek: (tekst) => polaczenie.clipboardPasteFrom(tekst),
          /**
           * ⚠️ `0xffe3` to lewy Ctrl, `0x76` to litera „v" w keysymach X11 — tak właśnie noVNC
           * nazywa klawisze w protokole. Wysyłamy parę wciśnięcie/puszczenie, bo program na
           * maszynie reaguje na CAŁY skrót, a nie na sam fakt, że coś przyszło.
           */
          wklej: () => {
            polaczenie.sendKey(0xffe3, 'ControlLeft', true);
            polaczenie.sendKey(0x76, 'KeyV', true);
            polaczenie.sendKey(0x76, 'KeyV', false);
            polaczenie.sendKey(0xffe3, 'ControlLeft', false);
          },
        });
        /**
         * ⚠️⚠️ ROZDZIELCZOŚĆ CZYTAMY Z PŁÓTNA, bo noVNC nie wystawia jej osobnym polem. Atrybuty
         * `width`/`height` płótna to DOKŁADNIE rozmiar bufora ramki przysłany przez maszynę —
         * nie rozmiar, w jakim to płótno jest rysowane (tym rządzi `scaleViewport`).
         */
        const plotno = ekran.current?.querySelector('canvas') ?? null;
        if (plotno !== null && plotno.width > 0 && plotno.height > 0) {
          onRozmiar?.({ szerokosc: plotno.width, wysokosc: plotno.height });
        }
      });
      polaczenie.addEventListener('disconnect', (e: CustomEvent<{ clean: boolean }>) => {
        if (!zywe) return;
        /** ⚠️ Odbieramy sterowanie, zanim ktokolwiek zdąży kliknąć w martwy już przycisk. */
        onSterowanie?.(null);
        setStan('KONIEC');
        setKomunikat(e.detail.clean ? t('pulpit.zamkniete') : t('pulpit.zerwane'));
      });
      polaczenie.addEventListener('securityfailure', (e: CustomEvent<{ reason?: string }>) => {
        if (!zywe) return;
        setStan('KONIEC');
        setKomunikat(e.detail.reason ?? t('pulpit.odmowa'));
      });
      rfb.current = polaczenie;
    })();

    return () => {
      zywe = false;
      /** ⚠️ Rozłączenie zamyka też sesję po stronie instancji i gasi serwer na maszynie. */
      try { rfb.current?.disconnect(); } catch { /* mogło już paść */ }
      rfb.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, machineId, t]);

  /**
   * ⚠️ PYTAMY, DOPÓKI NIE MA OBRAZU — i przestajemy, gdy obraz jest. Przy działającej sesji nie ma
   * o co pytać, a jedno żądanie na dwie sekundy przez cały podgląd byłoby ruchem bez treści.
   */
  useEffect(() => {
    if (stan === 'OBRAZ') return;
    const zegar = setInterval(() => {
      const id = idSesji.current;
      if (id === null) return;
      void stanPulpitu(groupId, machineId, id)
        .then((w) => {
          const zadanie =
            w.zadanie == null
              ? null
              : w.zadanie.error != null && w.zadanie.error !== ''
                ? `${t('pulpit.agentMowi')}: ${w.zadanie.error}`
                : w.zadanie.note != null && w.zadanie.note !== ''
                  ? `${t('pulpit.agentRobi')}: ${w.zadanie.note}`
                  : `${t('pulpit.zadanieStan')}: ${w.zadanie.status}`;
          setOdPortalu({ powod: w.powod ?? null, zadanie });
        })
        .catch(() => undefined);
    }, 2000);
    return () => clearInterval(zegar);
  }, [stan, groupId, machineId, t]);

  return (
    <div className="relative h-full min-h-0 bg-black">
      {/*
        ⚠️ KONTENER MUSI MIEĆ WŁASNĄ WYSOKOŚĆ — noVNC skaluje obraz do niego (obserwuje jego rozmiar
        `ResizeObserver`-em). Kontener bez wysokości ma zero pikseli i obraz „nie przychodzi",
        chociaż przychodzi i jest rysowany w prostokącie o zerowej wysokości.
      */}
      <div ref={ekran} className="h-full w-full" aria-label={t('pulpit.obszar')} />

      {/*
        ⚠️⚠️ KOMUNIKATY LEŻĄ NA OBRAZIE, A NIE NAD NIM. Własny pasek zabierałby wysokość ZAWSZE,
        także wtedy, gdy nie ma czego powiedzieć — a przy ekranie stojącym wysokość jest jedynym
        wymiarem, którego brakuje. Warstwa znika razem z ostatnim zdaniem.
      */}
      {stan !== 'OBRAZ' && (
        <div className="pointer-events-none absolute inset-x-0 top-0 space-y-1 bg-black/70 px-3 py-2 text-white">
          <p className="flex items-center gap-2 text-sm">
            {stan === 'KONIEC' ? <Unplug className="size-4" /> : <Loader2 className="size-4 animate-spin" />}
            {komunikat ?? (stan === 'OTWIERAM' ? t('pulpit.otwieram') : t('pulpit.czekamNaAgenta'))}
          </p>
          {/*
            ⚠️⚠️ TO JEST TA LINIJKA, KTÓREJ BRAKOWAŁO. „Połączenie zerwane" mówi tylko tyle, że
            gniazdo padło; dopiero powód z portalu i meldunek agenta rozstrzygają, CO padło —
            a bez tego każda próba wygląda tak samo i nie da się z niej niczego nauczyć.
          */}
          {odPortalu.powod !== null && <p className="text-[11px] text-[var(--warning)]">{odPortalu.powod}</p>}
          {odPortalu.zadanie !== null && <p className="break-words text-[11px] opacity-80">{odPortalu.zadanie}</p>}
          {/* ⚠️ Nazwa węzła zostaje na widoku — przy trzech maszynach wyspy to ona mówi, na którą patrzysz. */}
          {wezel !== null && <p className="text-[11px] opacity-60">{wezel}</p>}
        </div>
      )}
    </div>
  );
}
